import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Nullable, OrderFilterModel, OrderItemProductShortTableRow, OrderTableRow } from '@/Types';
import { AddressValidationResultType, OrderStatusFilter } from '@/Enums';
import { shippingLabelModalAsyncActions } from '@/ModalWindows/ShippingLabelModal/services';
import { salesAsyncActions } from './asyncActions';
import { splitOrdersModalAsyncActions } from '@/ModalWindows/SplitOrdersModal/services/asyncActions';
import { mergeOrdersAsyncActions } from '@/ModalWindows/MergeOrdersModal/services/asyncActions';
import { ModalDataType } from '@/Types/ModalDataType';
import { AddressModel } from '@/Models';
import { OrderNoteType } from '@/Enums/OrderNoteType';
import { salesUtils } from '@/Pages/Sales/services/utils';
import { orderMapping } from '@/Mapping/OrderMapping';
import { OrderModel } from '@/Models/Order/OrderModel';
import { DataTableSource } from '@/Components/DataTableV2/services/types';

type NoteModalType = ModalDataType<{
  saleId: number;
  noteType: OrderNoteType;
  noteValue: string;
}>;

type OrderModalType = ModalDataType<{
  order: Nullable<OrderTableRow>;
  modalType: 'create' | 'update' | 'copy';
}>;

type TabStepsType = {
  overviewTabStep: number;
  addressTabStep: number;
  orderModalStep: number;
};

export type SalesPageState = {
  sales: OrderTableRow[];
  selectedSale: Nullable<OrderTableRow>;
  selectedSales: OrderTableRow[];
  orderFilters: Nullable<OrderFilterModel>;
  tableStateKey: string;
  salesDataSource: DataTableSource<OrderTableRow> | null;

  filterUpdate: {
    storeFilterChanged: boolean;
    newOrderData: Nullable<OrderModel>;
  };
  modals: {
    orderFormModalData: OrderModalType;
    productModalData: ModalDataType<OrderItemProductShortTableRow>;
    orderFormPreviewData: Nullable<OrderTableRow>;
    cantChangeOrderDialog: ModalDataType<null>;
    deleteOrderDialog: ModalDataType<number>;
    copyOrderModal: ModalDataType<number>;
    noteModalData: NoteModalType;
    historyModalData: ModalDataType<null>;
  };
  ordersUpdate: {
    newSalesCount: number;
    updatedOrdersExists: boolean;
  };
  steps: TabStepsType;
};

const initialState: SalesPageState = {
  sales: [],
  selectedSale: null,
  selectedSales: [],
  orderFilters: null,
  tableStateKey: '',
  salesDataSource: null,

  filterUpdate: {
    newOrderData: null,
    storeFilterChanged: false,
  },
  modals: {
    orderFormModalData: { visible: false },
    productModalData: { visible: false },
    orderFormPreviewData: null,
    cantChangeOrderDialog: { visible: false },
    deleteOrderDialog: { visible: false },
    copyOrderModal: { visible: false },
    noteModalData: { visible: false },
    historyModalData: { visible: false },
  },
  ordersUpdate: {
    updatedOrdersExists: false,
    newSalesCount: 0,
  },
  steps: {
    overviewTabStep: 0,
    addressTabStep: 0,
    orderModalStep: 0,
  },
};

const salesPageSlice = createSlice({
  name: 'salesPage',
  initialState,
  reducers: {
    setHistoryModalData: (state, action: PayloadAction<ModalDataType<null>>) => {
      state.modals.historyModalData = action.payload;
    },
    setNoteModalData: (state, action: PayloadAction<NoteModalType>) => {
      state.modals.noteModalData = action.payload;
    },
    setTabActiveStep: (state, action: PayloadAction<{ key: keyof TabStepsType; value: number }>) => {
      state.steps[action.payload.key] = action.payload.value;
    },
    setSelectedSale: (state, action: PayloadAction<Nullable<OrderTableRow>>) => {
      state.selectedSale = state.sales.find((sale) => sale.id == action.payload?.id) ?? null;
    },
    setSelectedSales: (state, action: PayloadAction<OrderTableRow[]>) => {
      state.selectedSales = action.payload;
    },
    setOrderFilters: (state, action: PayloadAction<OrderFilterModel>) => {
      state.orderFilters = action.payload;
    },
    setTableStateKey: (state, action: PayloadAction<string>) => {
      state.tableStateKey = action.payload;
    },
    showOrderModal: (
      state,
      action: PayloadAction<{ modalType: 'create' | 'update' | 'copy'; order?: OrderTableRow }>,
    ) => {
      const { order, modalType } = action.payload;

      if (state.selectedSale?.currentInvoiceNumber && modalType === 'update') {
        state.modals.cantChangeOrderDialog = { visible: true };
        return;
      } // if

      let selectedSale: Nullable<OrderTableRow> = null;

      if (modalType === 'update') {
        selectedSale = state.selectedSale;
      } // if

      state.modals.orderFormModalData = {
        visible: true,
        data: {
          order: order ?? selectedSale,
          modalType: modalType,
        },
      };
    },
    closeOrderModal: (state) => {
      state.modals.orderFormModalData = { visible: false };
    },
    setOrderProductModal: (state, action: PayloadAction<ModalDataType<OrderItemProductShortTableRow>>) => {
      state.modals.productModalData = action.payload;
    },
    closeCantChangeOrderDialog: (state) => {
      state.modals.cantChangeOrderDialog = { visible: false };
    },
    setDeleteOrderDialog: (state, action: PayloadAction<ModalDataType<number>>) => {
      state.modals.deleteOrderDialog = action.payload;
    },
    setCopyOrderModal: (state, action: PayloadAction<boolean>) => {
      state.modals.copyOrderModal = { visible: action.payload };
    },
    setNewSalesCount: (state, action: PayloadAction<number>) => {
      state.ordersUpdate.newSalesCount = action.payload;
    },
    addNewSalesCount: (state, action: PayloadAction<number>) => {
      state.ordersUpdate.newSalesCount += action.payload;
    },
    setUpdatedOrdersExists: (state) => {
      state.ordersUpdate.updatedOrdersExists = true;
    },
    ordersUpdate: (state, action: PayloadAction<OrderModel[]>) => {
      const updatedRows = orderMapping.convertOrdersToOrderTableRows(action.payload);
      salesUtils.handleOrdersUpdate(state, updatedRows);
    },
    updateValidatedAddress: (state, action: PayloadAction<{ orderId: number; appliedAddress: AddressModel }>) => {
      const { appliedAddress, orderId } = action.payload;
      const orderRow = state.sales.find((item) => item.id === orderId);

      if (orderRow) {
        const updatedRow = orderMapping.updateShippingAddress(orderRow, appliedAddress);
        salesUtils.handleOrdersUpdate(state, [updatedRow]);
      }
    },

    removeOrdersFromStateByCurrentFilters: (
      state,
      action: PayloadAction<{ filters: readonly OrderStatusFilter[]; orderIds: number[] }>,
    ) => {
      if (!state.orderFilters) return;

      const { filters, orderIds } = action.payload;

      const isActiveFilter = filters.includes(state.orderFilters.orderStatusFilter);

      if (isActiveFilter) {
        state.salesDataSource?.remove(orderIds);
        state.sales = state.sales.filter((sale) => !orderIds.includes(sale.id));
        state.selectedSales = state.selectedSales.filter((sale) => !orderIds.includes(sale.id));

        if (state.selectedSale && orderIds.includes(state.selectedSale.id)) {
          state.selectedSale = null;
        }
      }
    },
  },

  extraReducers: (builder) => {
    builder.addCase(salesAsyncActions.getSalesFromElastic.fulfilled, (state, action) => {
      const { items: orders, totalEntriesCount } = action.payload;
      const orderRows = orderMapping.convertOrdersToOrderTableRows(orders);

      // Filter update
      if (state.filterUpdate.storeFilterChanged) {
        const newOrder = state.filterUpdate?.newOrderData;
        const orderExist = orders.find((item) => item.id == newOrder?.id);

        if (!orderExist && newOrder) {
          orders.unshift(newOrder);
        }

        state.filterUpdate = { storeFilterChanged: false, newOrderData: null };
      }

      state.sales = orderRows;
      state.ordersUpdate = { newSalesCount: 0, updatedOrdersExists: false };

      if (state.selectedSale) {
        state.selectedSale = state.sales.find((x) => x.id === state.selectedSale?.id) ?? null;
      }
      if (state.selectedSales.length) {
        state.selectedSales = state.selectedSales
          .map((selectedSale) => state.sales.find((s) => s.id === selectedSale.id))
          .filter((selectedSale): selectedSale is OrderTableRow => !!selectedSale);
      }

      if (state.salesDataSource) {
        state.salesDataSource.clear();
        state.salesDataSource.insert(orderRows);
        state.salesDataSource.totalCount(totalEntriesCount);
      } else {
        state.salesDataSource = new DataTableSource<OrderTableRow>('id', orderRows, totalEntriesCount);
      }
    });

    builder.addCase(shippingLabelModalAsyncActions.applyAddressChanges.fulfilled, (state, action) => {
      const { order } = action.payload;
      const orderRow = state.sales.find((item) => item.id === order.id);

      if (orderRow) {
        const updatedRow = orderMapping.copyShippingInfo(orderRow, order);
        salesUtils.handleOrdersUpdate(state, [updatedRow]);
      }
    });

    builder.addCase(shippingLabelModalAsyncActions.validateOrderAddresses.fulfilled, (state, action) => {
      const filteredAddresses = action.payload.compares.filter((c) => {
        return c.result === AddressValidationResultType.FoundWithFullCompliance;
      });

      const updatedRows = state.sales
        .filter((order) => {
          return filteredAddresses.find((address) => address.orderId == order.id);
        })
        .map((order) => {
          const match = filteredAddresses.find((f) => f.orderId === order.id);

          if (match) {
            return orderMapping.updateShippingAddress(order, match.suggestedAddress);
          } else {
            return order;
          }
        });

      salesUtils.handleOrdersUpdate(state, updatedRows);
    });

    builder.addCase(salesAsyncActions.createOrder.fulfilled, (state, action) => {
      salesUtils.handleOrderCreation(state, action.payload);
    });

    builder.addCase(salesAsyncActions.copyOrder.fulfilled, (state, action) => {
      salesUtils.handleOrderCreation(state, action.payload);
    });

    builder.addCase(salesAsyncActions.updateOrder.fulfilled, (state, action) => {
      const updatedRows = orderMapping.convertOrdersToOrderTableRows([action.payload]);

      state.modals.orderFormModalData = { visible: false };
      salesUtils.handleOrdersUpdate(state, updatedRows);
    });

    builder.addCase(salesAsyncActions.getOrdersByIds.fulfilled, (state, action) => {
      const updatedRows = orderMapping.convertOrdersToOrderTableRows(action.payload);
      salesUtils.handleOrdersUpdate(state, updatedRows);
    });

    builder.addCase(salesAsyncActions.updateOrderNote.fulfilled, (state, action) => {
      const updatedRows = orderMapping.convertOrdersToOrderTableRows([action.payload.order]);
      salesUtils.handleOrdersUpdate(state, updatedRows);
    });

    builder.addCase(splitOrdersModalAsyncActions.splitOrder.fulfilled, (state, action) => {
      const { newOrders, splittedOrderId } = action.payload;
      const newOrderRows = orderMapping.convertOrdersToOrderTableRows(newOrders);

      const filteredItems = state.sales.filter((sale) => {
        return sale.id !== splittedOrderId;
      });

      state.sales = [...newOrderRows, ...filteredItems];
      state.salesDataSource?.remove([splittedOrderId]);
      state.salesDataSource?.insert(newOrderRows);
    });

    builder.addCase(mergeOrdersAsyncActions.mergeOrders.fulfilled, (state, action) => {
      const { mergedOrder, mergeOrdersIds } = action.payload;
      const newOrderRow = orderMapping.convertOrderToOrderTableRow(mergedOrder);

      const filteredItems = state.sales.filter((sale) => {
        return !mergeOrdersIds.some((id) => id === sale.id);
      });

      state.sales = [newOrderRow, ...filteredItems];
      state.salesDataSource?.remove(mergeOrdersIds);
      state.salesDataSource?.insert([newOrderRow]);
    });

    builder.addMatcher(
      (action) =>
        [
          salesAsyncActions.createOrderPreview.fulfilled.type,
          salesAsyncActions.updateOrderPreview.fulfilled.type,
          salesAsyncActions.copyOrderPreview.fulfilled.type,
        ].includes(action.type),
      (state, action) => {
        state.modals.orderFormPreviewData = salesUtils.convertOrderPreviewData(action.payload);
      },
    );
  },
});

export const salesPageReducer = salesPageSlice.reducer;
export const salesPageActions = salesPageSlice.actions;
export const salesPageAsyncActions = salesAsyncActions;
